<script>
// Cards dos 3 regimes (comissionado / fee-based / consultor). Cada card mostra
// só 4 números (custo do cliente e receita de quem atende, cada um em % a.a. e
// R$/ano) — subtítulo descritivo e as sub-linhas de decomposição saem do CORPO
// do card e vivem em tooltip, a pedido do CTO ("muito texto").
//
// Quem atende é nomeado pelo CANAL do cenário, nos três cards (`rotuloReceita`,
// termo de `constants/baldes.js`): na consultoria não há assessor a quem
// creditar a receita. O NOME do regime não segue o canal — "Fee fixo ·
// assessoria" nomeia o modelo, não o cargo.
//
// São 3 tooltips, cada um com conteúdo distinto (owner, 2026-08-03: "deixa
// então na receita do assessor um ícone de informação explicando o racional
// que chegou no número, mesma coisa no custo"):
//
// - no NOME do regime: o que aquele regime é (subtítulo + TR de investimentos);
// - no CUSTO DO CLIENTE: a composição, com as parcelas somando o total exibido;
// - na RECEITA de quem atende: a cadeia, degrau a degrau, terminando no exibido.
//
// Isso não contradiz o pedido do CTO: o que poluía era texto solto no corpo do
// card. Tooltip é opt-in — e um por número é o que permite conferir CADA número
// sem ler o racional do outro.

import { REGIME_COLORS } from '@modules/fee-fixo/constants/feeLab'
import {
  CANAL_PADRAO,
  regimeDoCanal,
  termoDoProfissional,
} from '@modules/fee-fixo/constants/baldes'
import {
  pct,
  asPct,
  fmtBRLk,
  rPerYear,
} from '@modules/fee-fixo/utils/fee-lab-format'

// Metadados estáticos por regime: nome, subtítulo descritivo e chip fixo de
// identidade (conteúdo + cor). `highlight` só existe
// no Consultor CVM — dispara a borda completa na cor primary do escritório em
// vez do border-left de 4px que os outros 2 regimes mantêm.
const REGIMES = [
  {
    key: 'comis',
    nome: 'Comissionado',
    // ★ 12/08/2026 (owner): a simulação passou a considerar só investimentos, e
    // o subtítulo prometia uma parcela que o card não soma mais.
    subtitulo: 'Take rate XP por classe, só investimentos',
    // ★ 21/09/2026 (owner, relato de usuário): o chip "legado" não fazia
    // sentido — comissionado é como o cliente é atendido hoje, não um
    // sistema antigo. Sem chip; os outros dois regimes mantêm o deles.
    chipContent: '',
    chipColor: '',
  },
  {
    key: 'fee',
    nome: 'Fee fixo · assessoria',
    // [D7-FINAL/US-4] Dizia "Plataforma retém parte do fee" — a mesma confusão
    // do áudio 1 sobrevivendo em mais uma superfície, e a US-4 a trocou por
    // "cobrada do CLIENTE por cima" (guia p.21).
    //
    // ★ 11/08/2026 (owner): o modelo virou, e o subtítulo com ele. A taxa É
    // retida de dentro do fee — ou seja, a frase de 2026-08-03 voltou a ser a
    // certa. O que a [US-4] travou continua valendo e está dito: a taxa é da
    // PLATAFORMA, não de quem atende. O subtítulo diz as duas coisas de uma vez
    // porque foi a omissão da segunda que criou a confusão original.
    subtitulo: 'Plataforma retém parte do fee; o cliente paga o mesmo',
    chipContent: 'fee-based',
    chipColor: REGIME_COLORS.fee,
  },
  {
    key: 'cons',
    nome: 'Consultor CVM',
    subtitulo: 'Fiduciário - fica com o fee cheio',
    chipContent: '+ receita líq.',
    chipColor: 'primary',
  },
]

// Chip fixo de identidade do regime (legado/fee-based/+ receita líq.), no
// lugar dos badges dinâmicos de comparação (mais barato/+ receita líq.
// condicional) que existiam antes. A lógica dinâmica
// (cheapestKeys/topAdvKeys, abaixo) continua calculada — só não é renderizada
// enquanto esta flag for true. Pra voltar ao comportamento antigo, troque só
// este valor pra false.
const USAR_CHIP_FIXO_DE_REGIME = true

// Chaves cujo valor produz a MESMA string exibida (via asPct) que o valor
// extremo — garante que dois cards com o mesmo número na tela sempre
// recebam o mesmo badge, mesmo que os floats brutos divirjam na 3ª casa.
const keysComValorExibidoIgual = (entries, alvo) => {
  const alvoExibido = asPct(alvo)
  return entries.filter((e) => asPct(e.v) === alvoExibido).map((e) => e.key)
}

export default {
  name: 'FeeLabRegimeCards',
  props: {
    resultado: {
      type: Object,
      default: null,
    },
    auc: {
      type: Number,
      default: 0,
    },
    // [D8] O modelo de atendimento do cenário. O destaque era FIXO no
    // Consultor CVM, o que passou a contradizer o default que o owner
    // definiu — os três cards continuam visíveis (a comparação é o produto),
    // mas o escolhido é o que a tela trata como o caso do cliente.
    canal: {
      type: String,
      default: CANAL_PADRAO,
    },
  },
  data() {
    return {
      usarChipFixoDeRegime: USAR_CHIP_FIXO_DE_REGIME,
      // Cor do badge soft: verde p/ "mais barato", azul p/ "+ receita líq.".
      // Usado só quando usarChipFixoDeRegime é false (comportamento antigo).
      badgeColors: { cheap: REGIME_COLORS.fee, adv: REGIME_COLORS.cons },
    }
  },
  computed: {
    /**
     * Quem recebe a receita, pelo canal do cenário (owner, 2026-08-03).
     *
     * Vale nos TRÊS cards: eles comparam REGIMES, mas quem embolsa é sempre o
     * profissional do cenário — no canal de consultoria não há assessor a
     * quem creditar, nem no card do consultor.
     */
    rotuloReceita() {
      return 'Receita do ' + termoDoProfissional(this.canal)
    },
    cheapestKeys() {
      const r = this.resultado
      if (!r || !r.cost) return []
      const entries = REGIMES.map((rg) => ({ key: rg.key, v: r.cost[rg.key] }))
      const min = entries.reduce((a, b) => (b.v < a.v ? b : a))
      return keysComValorExibidoIgual(entries, min.v)
    },
    topAdvKeys() {
      const r = this.resultado
      if (!r || !r.adv) return []
      const entries = REGIMES.map((rg) => ({ key: rg.key, v: r.adv[rg.key] }))
      const max = entries.reduce((a, b) => (b.v > a.v ? b : a))
      return keysComValorExibidoIgual(entries, max.v)
    },
    cards() {
      const r = this.resultado
      if (!r || !r.cost || !r.adv) return []
      const regimeEscolhido = regimeDoCanal(this.canal)
      return REGIMES.map((rg) => {
        const break_ = (r.costBreak && r.costBreak[rg.key]) || {}
        // [D8] Cada card mostra o custo do SEU regime, direto de `cost` — que
        // desde a D8 já traz a taxa de plataforma dentro da família assessoria
        // (134,5) e nada no consultor (119,5). O desvio por `total_assessor`
        // que a D7-FINAL criou saiu junto com o campo: ele existia porque o
        // total era o do consultor nos dois, e agora não é mais.
        const cost = r.cost[rg.key]
        const adv = r.adv[rg.key]
        const trInvBps = (r.com && r.com.capt) || 0
        const feeTotal = (r.feeTotal && r.feeTotal[rg.key]) || 0
        return {
          key: rg.key,
          nome: rg.nome,
          subtitulo: rg.subtitulo,
          chipContent: rg.chipContent,
          chipColor: rg.chipColor,
          highlight: rg.key === regimeEscolhido,
          custoPct: asPct(cost) + ' a.a.',
          custoAno: fmtBRLk(rPerYear(cost, this.auc)) + '/ano',
          trInvLabel: 'TR investimentos: ' + asPct(trInvBps),
          // spec-021 [D9]: abertura do custo entre quem captura (plataforma) e
          // quem retém (gestores) — só existe onde o backend manda `capt`/`gest`
          // no costBreak; buildDecomp devolve '' sem eles.
          decomposicao: this.buildDecomp(break_, r.H),
          advPct: asPct(adv) + ' a.a.',
          advAno: fmtBRLk(rPerYear(adv, this.auc)) + '/ano',
          // O racional que produziu a receita, degrau a degrau (owner,
          // 2026-08-03: "um ícone de informação explicando o racional que
          // chegou no número"). Sai da `cadeia` que o motor publica — a tela
          // não refaz nenhuma multiplicação.
          receitaRacional: this.buildReceitaRacional(
            rg.key,
            r.advCadeia,
            adv,
            feeTotal
          ),
          cheapest: this.cheapestKeys.includes(rg.key),
          topAdv: this.topAdvKeys.includes(rg.key),
        }
      })
    },
  },
  methods: {
    /**
     * Abertura dos produtos entre quem captura e quem retém (spec-021, [D9]).
     *
     * `capt` e `gest` SOMAM `ti` — não se somam a ele. Por isso vêm entre
     * parênteses, dentro da linha de produtos: listá-los como parcelas irmãs
     * faria a decomposição exibir o custo duas vezes.
     */
    buildAberturaProdutos(b) {
      if (!b.gest) return ''
      return (
        ' (plataforma ' + asPct(b.capt) + ' + gestores ' + asPct(b.gest) + ')'
      )
    },
    buildDecomp(b, H) {
      if (!b || b.total == null) return ''
      const parts = [
        'Produtos ' + asPct(b.ti || 0) + this.buildAberturaProdutos(b),
      ]
      // ★ 12/08/2026 (owner): a simulação passou a considerar só investimentos.
      // A parcela é CONDICIONAL, não removida: cenário salvo antes da decisão
      // tem `ni` dentro do próprio total, e suprimi-la ali faria o tooltip
      // fechar 42 bps abaixo do card — o mesmo defeito que a taxa da plataforma
      // produziu em 11/08, com outro nome.
      if (b.ni) parts.push('Não Investimentos ' + asPct(b.ni))
      if (b.fee) parts.push('Fee ' + asPct(b.fee))
      if (b.cb) parts.push('− Cashback ' + asPct(b.cb))
      // ★ 11/08/2026 (owner): a parcela "Taxa de plataforma" SAIU desta soma.
      //
      // A [D8] a tinha acrescentado porque o total do regime assessoria a
      // continha, e sem ela o tooltip fechava 15 bps abaixo do card. Com a taxa
      // retida de dentro do fee, o total voltou a não contê-la — e mantê-la
      // aqui inverteu o defeito: o tooltip somava 1,35% sob um card de 1,20%.
      //
      // A taxa não sumiu da tela: ela aparece na lente por DESTINO e no
      // racional da receita (`buildReceitaRacional`), que é onde ela de fato
      // muda alguma coisa. Aqui ela seria uma parcela que o cliente não paga.
      //
      // ⚠️ Este `buildDecomp` roda também sobre cenário SALVO na era [D8], cujo
      // total ainda contém a taxa. Nesse caso o tooltip fecha 15 bps abaixo do
      // card — o mesmo desenho do backend, que data o cenário pela identidade
      // (`_taxa_ja_somada_ao_total`). Se isso incomodar na tela, o conserto é
      // datar aqui pela mesma identidade, não devolver a parcela.
      const anoLabel = H ? ' (Ano ' + H + ')' : ''
      return (
        'Custo: ' +
        asPct(b.total) +
        anoLabel +
        ' = ' +
        parts.join(' + ').replace('+ −', '−')
      )
    },
    /**
     * O fator que o motor aplicou entre dois degraus, como percentual.
     *
     * DERIVADO dos degraus publicados, e não lido das premissas de hoje: o
     * cenário salvo congela as premissas com que foi apresentado ([D5.2] da
     * spec-021), e um tooltip que anunciasse "imposto 19,9%" ao lado de uma
     * conta feita com 15% seria a próxima fonte de desconfiança. Dividir dois
     * números que o motor publicou não recalcula o valor — só nomeia a razão
     * entre eles.
     *
     * ★ 12/08/2026 (owner): *"do consultor também entra repasse"*. O último
     * degrau sempre foi o repasse ao profissional, mas vinha rotulado "do
     * líquido" — descrevia a RAZÃO (adv ÷ líquido) sem dizer o que ela é. Com
     * o texto anterior negando o repasse na consultoria, o rótulo genérico
     * deixava a dúvida de pé: o número aplicava o repasse e a frase não o
     * nomeava em lugar nenhum.
     *
     * @returns {string} ` (X% do ...)`, ou '' quando não há como derivar
     *   (denominador zero — carteira 100% em float, por exemplo). Omitir o
     *   fator mantém os degraus, que são fato, sem inventar um `NaN%`.
     */
    buildFatorDoDegrau(numerador, denominador, sufixo, casas = 0) {
      if (!denominador) return ''
      return ' (' + pct(numerador / denominador, casas) + ' ' + sufixo + ')'
    },
    /**
     * O racional que chegou na receita exibida (`rotuloReceita`), degrau a
     * degrau.
     *
     * Owner, 2026-08-03: *"deixa então na receita do assessor um ícone de
     * informação explicando o racional que chegou no número"*. Substitui a
     * linha-resumo anterior (`buildReceitaSubLabel`), que dizia só a fração
     * final: quem não entende de onde vem o número não se convence com outra
     * razão sem os degraus que a produziram.
     *
     * Os três regimes terminam no valor EXIBIDO no card — é o fechamento que
     * torna o tooltip conferível em vez de mais um número para desconfiar.
     *
     * @param {string} key Regime (`comis` | `fee` | `cons`).
     * @param {Object|null} cadeia `resultado.advCadeia`; `null` em cenário
     *   salvo antes da spec-022, que usou outro modelo.
     * @param {number} adv A receita publicada para o regime.
     * @param {number} feeTotal O fee cobrado do cliente no regime.
     */
    buildReceitaRacional(key, cadeia, adv, feeTotal) {
      const valorFinal = asPct(adv)
      if (!cadeia) {
        // Ausência é informação: o cenário foi calculado pelo modelo anterior
        // (repasse único), e desenhar aqui os degraus de hoje descreveria uma
        // conta que aquele número nunca teve.
        return (
          'Calculado pelo modelo com que o cenário foi salvo — repasse único, ' +
          'sem os degraus de plataforma e imposto → ' +
          valorFinal
        )
      }
      if (key === 'cons') {
        // ★ Corrigido em 11/08/2026. O texto anterior — "repasse ao escritório
        // e imposto não entram neste regime" — descrevia o modelo de UM degrau
        // que a reversão de 10/08 (p.27 do relatório) desfez: a consultoria CVM
        // apura imposto e reparte com quem atende exatamente como a assessoria.
        // O que ela NÃO tem é a taxa da plataforma, e é só isso que a separa.
        //
        // Lê `consultoria_liquida_de_imposto_bps`, e não
        // `fee_liquido_de_imposto_bps`: desde que a perna do fee passou a
        // descontar a taxa, aquela chave vale 36,045 (o número do ASSESSOR) e
        // usá-la aqui mostraria a cadeia do assessor sob o rótulo do consultor.
        const liquido = cadeia.consultoria_liquida_de_imposto_bps
        return (
          'Fee cobrado do cliente ' +
          asPct(feeTotal) +
          ' → o consultor cobra direto, sem taxa de plataforma; depois do ' +
          'imposto, ' +
          asPct(liquido) +
          this.buildFatorDoDegrau(feeTotal - liquido, feeTotal, 'de imposto', 1) +
          ' → ao profissional ' +
          valorFinal +
          this.buildFatorDoDegrau(adv, liquido, 'de repasse ao profissional')
        )
      }
      if (key === 'fee') {
        // ★ 11/08/2026: o degrau da plataforma passou a existir aqui.
        //
        // Até então `fee_ao_escritorio_bps` era o fee cheio e o texto dizia
        // "entra inteiro no escritório" — as duas coisas viraram falsas na
        // mesma decisão: a chave agora é LÍQUIDA da taxa (45 no default), e o
        // que entra no escritório não é o que o cliente paga (60). Mantido o
        // texto antigo, o tooltip anunciaria "Fee cobrado do cliente 0,45% →
        // entra inteiro no escritório" — errado no valor E na afirmação, e sem
        // nenhum número ficando vermelho, porque a soma dos degraus continua
        // fechando no valor final.
        //
        // O fee cheio é reconstituído das duas chaves publicadas em vez de lido
        // de `feeTotal`: assim o degrau da plataforma e o valor de partida vêm
        // da MESMA fonte, e não podem divergir entre si.
        const taxa = cadeia.taxa_da_plataforma_bps || 0
        const feeCheio = cadeia.fee_ao_escritorio_bps + taxa
        const degrauDaPlataforma = taxa
          ? ' → a plataforma retém ' +
            asPct(taxa) +
            this.buildFatorDoDegrau(taxa, feeCheio, 'do fee', 1) +
            ' e ao escritório chegam ' +
            asPct(cadeia.fee_ao_escritorio_bps)
          : ' → entra inteiro no escritório'
        return (
          'Fee cobrado do cliente ' +
          asPct(feeCheio) +
          degrauDaPlataforma +
          ' e, depois do imposto, ' +
          asPct(cadeia.fee_liquido_de_imposto_bps) +
          this.buildFatorDoDegrau(
            cadeia.fee_ao_escritorio_bps - cadeia.fee_liquido_de_imposto_bps,
            cadeia.fee_ao_escritorio_bps,
            'de imposto',
            1
          ) +
          ' → ao profissional ' +
          valorFinal +
          this.buildFatorDoDegrau(
            cadeia.fee_bps,
            cadeia.fee_liquido_de_imposto_bps,
            'de repasse ao profissional'
          )
        )
      }
      return (
        'Captura de investimentos ' +
        asPct(cadeia.captura_investimentos_bps) +
        ' → a plataforma repassa ' +
        asPct(cadeia.repasse_ao_escritorio_bps) +
        this.buildFatorDoDegrau(
          cadeia.repasse_ao_escritorio_bps,
          cadeia.captura_investimentos_bps,
          'da captura'
        ) +
        ' → depois do imposto do escritório, ' +
        asPct(cadeia.liquido_de_imposto_bps) +
        this.buildFatorDoDegrau(
          cadeia.repasse_ao_escritorio_bps - cadeia.liquido_de_imposto_bps,
          cadeia.repasse_ao_escritorio_bps,
          'de imposto',
          1
        ) +
        ' → ao profissional ' +
        valorFinal +
        this.buildFatorDoDegrau(
          cadeia.comissionado_bps,
          cadeia.liquido_de_imposto_bps,
          'de repasse ao profissional'
        )
      )
    },
  },
}
</script>

<template>
  <div class="regime-cards">
    <div
      v-for="card in cards"
      :key="card.key"
      class="regime-cards__card az-card-bordered"
      :class="{ 'regime-cards__card--highlight': card.highlight }"
    >
      <div
        class="regime-cards__head d-flex align-center justify-space-between mb-2"
      >
        <div class="d-flex align-center az-gap-xxsmall">
          <span class="az-text-primary az-size-14 font-semibold">{{
            card.nome
          }}</span>
          <v-tooltip right max-width="280">
            <template v-slot:activator="{ on, attrs }">
              <span
                class="regime-cards__info d-inline-flex"
                v-bind="attrs"
                v-on="on"
              >
                <icon-svg name="info-circle" size="14" color="secondary" />
              </span>
            </template>
            <div>
              <div>{{ card.subtitulo }}</div>
              <div class="mt-1">{{ card.trInvLabel }}</div>
            </div>
          </v-tooltip>
        </div>
        <div class="d-flex align-center az-gap-xsmall">
          <template v-if="usarChipFixoDeRegime">
            <BaseStatus
              v-if="card.chipContent"
              soft
              :content="card.chipContent"
              :color="card.chipColor"
              preserve-case
            />
          </template>
          <template v-else>
            <BaseStatus
              v-if="card.cheapest"
              soft
              content="mais barato"
              :color="badgeColors.cheap"
              preserve-case
            />
            <BaseStatus
              v-if="card.topAdv"
              soft
              content="+ receita líq."
              :color="badgeColors.adv"
              preserve-case
            />
          </template>
        </div>
      </div>

      <div class="d-flex align-center az-gap-xxsmall">
        <span class="regime-cards__section-label az-text-small"
          >Custo do cliente</span
        >
        <v-tooltip right max-width="280">
          <template v-slot:activator="{ on, attrs }">
            <span
              class="regime-cards__info d-inline-flex"
              data-test="info-custo"
              v-bind="attrs"
              v-on="on"
            >
              <icon-svg name="info-circle" size="12" color="secondary" />
            </span>
          </template>
          <div>{{ card.decomposicao }}</div>
        </v-tooltip>
      </div>
      <div class="regime-cards__cost d-flex align-baseline az-gap-small">
        <span class="az-title-secondary regime-cards__cost-value">{{
          card.custoPct
        }}</span>
        <span class="az-text-small">{{ card.custoAno }}</span>
      </div>

      <v-divider class="my-4" />

      <div class="d-flex align-center az-gap-xxsmall">
        <span
          class="regime-cards__section-label az-text-small"
          data-test="rotulo-receita"
          >{{ rotuloReceita }}</span
        >
        <v-tooltip right max-width="280">
          <template v-slot:activator="{ on, attrs }">
            <span
              class="regime-cards__info d-inline-flex"
              data-test="info-receita"
              v-bind="attrs"
              v-on="on"
            >
              <icon-svg name="info-circle" size="12" color="secondary" />
            </span>
          </template>
          <div>{{ card.receitaRacional }}</div>
        </v-tooltip>
      </div>
      <div class="regime-cards__cost d-flex align-baseline az-gap-small">
        <span class="az-title-secondary regime-cards__cost-value">
          {{ card.advPct }}
        </span>
        <span class="az-text-small">{{ card.advAno }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.regime-cards
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr))
  gap: 12px

.regime-cards__card
  // Preenche a altura da linha do grid — todos os cards com a mesma altura.
  height: 100%

// Altura fixa do header pra o chip (BaseStatus) não deixar o card mais alto.
.regime-cards__head
  min-height: 28px

// Ativador dos tooltips de racional. É um <span> (elemento NATIVO) e não o
// `icon-svg` direto de propósito: em Vue 2, `v-on` num componente registra
// custom events, e o `<svg>` de `icons/svg/info-circle.vue` não repassa
// `$listeners` — o tooltip nunca abriria, com a suíte verde. O span recebe os
// listeners nativos do Vuetify e o ícone só desenha.
.regime-cards__info
  cursor: help
  line-height: 0

.regime-cards__cost
  cursor: default
  margin: 8px 0

// Label de seção em caps — mesmo padrão já usado em fee-lab-composicao-custo.vue
// (__overlay-label): uppercase via CSS, não texto literal em caixa alta no DOM.
.regime-cards__section-label
  text-transform: uppercase
  letter-spacing: 0.04em
  font-size: 10px
  color: var(--neutral-400)

// Destaque do Consultor CVM: borda completa na cor primary do escritório —
// esquerda mais grossa que os outros 3 lados (mesma ideia de accent lateral
// que os cards tinham antes, só que como parte de uma borda inteira em vez
// de um "adesivo" de cor separado).
.regime-cards__card--highlight
  border: 2px solid var(--primary)
</style>
